import { create } from "zustand";
import { persist } from "zustand/middleware";
import { pageService } from "@/services/pageService";
import {
  Page,
  CreatePageDto,
  UpdatePageDto,
  GetPaginationPagesDto,
} from "@/types/page";
import { showToast } from "@/lib/toast";

interface PageState {
  // State
  pages: Page[];
  selectedPage: Page | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  isLoadingDetail: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;

  // Actions
  fetchPages: (params?: GetPaginationPagesDto) => Promise<void>;
  fetchPageById: (id: string) => Promise<void>;
  createPage: (data: CreatePageDto) => Promise<void>;
  updatePage: (id: string, data: UpdatePageDto) => Promise<void>;
  deletePage: (id: string) => Promise<void>;
  changeStatus: (id: string, status: string) => Promise<void>;
  toggleDisplay: (id: string) => Promise<void>;
  setSelectedPage: (page: Page | null) => void;
  clearSelectedPage: () => void;
  clearError: () => void;
  setCurrentPage: (page: number) => void;
}

export const usePageStore = create<PageState>()(
  persist(
    (set, get) => ({
      // Initial state
      pages: [],
      selectedPage: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },
      isLoading: false,
      isLoadingDetail: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      error: null,

      // Fetch pages with pagination
      fetchPages: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response: any = await pageService.getPaginationPages(params);
          if (response.ok && response.data) {
            set({
              pages: response.data?.data?.data.pages,
              pagination: response.data?.data?.data.pagination,
              isLoading: false,
            });
          } else {
            set({
              error: response.message || "Lỗi khi tải danh sách trang",
              isLoading: false,
            });
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tải danh sách trang",
            isLoading: false,
          });
        }
      },

      // Fetch page by ID
      fetchPageById: async (id: string) => {
        set({ isLoadingDetail: true, error: null });
        try {
          const response: any = await pageService.getPageById(id);
          if (response.ok && response.data) {
            set({
              selectedPage: response.data?.data?.data,
              isLoadingDetail: false,
            });
          } else {
            set({
              error: response.message || "Lỗi khi tải trang",
              isLoadingDetail: false,
            });
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tải trang",
            isLoadingDetail: false,
          });
        }
      },

      // Create page
      createPage: async (data: CreatePageDto) => {
        set({ isCreating: true, error: null });
        try {
          const response: any = await pageService.create(data);
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchPages({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isCreating: false });
            showToast.success("Tạo trang thành công");
          } else {
            set({
              error: response.message || "Lỗi khi tạo trang",
              isCreating: false,
            });
            showToast.error(response.message || "Lỗi khi tạo trang");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tạo trang",
            isCreating: false,
          });
          showToast.error(error.message || "Lỗi khi tạo trang");
        }
      },

      // Update page
      updatePage: async (id: string, data: UpdatePageDto) => {
        set({ isUpdating: true, error: null });
        try {
          const response: any = await pageService.updatePage(id, data);
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchPages({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isUpdating: false });
            showToast.success("Cập nhật trang thành công");
          } else {
            set({
              error: response.message || "Lỗi khi cập nhật trang",
              isUpdating: false,
            });
            showToast.error(response.message || "Lỗi khi cập nhật trang");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi cập nhật trang",
            isUpdating: false,
          });
          showToast.error(error.message || "Lỗi khi cập nhật trang");
        }
      },

      // Delete page
      deletePage: async (id: string) => {
        set({ isDeleting: true, error: null });
        try {
          const response: any = await pageService.deletePage(id);
          if (response.ok) {
            const { pages } = get();
            const filteredPages = pages.filter((page) => page._id !== id);
            set({
              pages: filteredPages,
              selectedPage: null,
              isDeleting: false,
            });
            showToast.success("Xóa trang thành công");
          } else {
            set({
              error: response.message || "Lỗi khi xóa trang",
              isDeleting: false,
            });
            showToast.error(response.message || "Lỗi khi xóa trang");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi xóa trang",
            isDeleting: false,
          });
          showToast.error(error.message || "Lỗi khi xóa trang");
        }
      },

      // Change page status
      changeStatus: async (id: string, status: string) => {
        set({ isUpdating: true, error: null });
        try {
          const response: any = await pageService.changeStatus(id, status);
          if (response.ok && response.data) {
            const { pages } = get();
            const updatedPages = pages.map((page) =>
              page._id === id ? response.data : page
            );
            set({
              pages: updatedPages,
              selectedPage: response.data,
              isUpdating: false,
            });
            showToast.success("Thay đổi trạng thái thành công");
          } else {
            set({
              error: response.message || "Lỗi khi thay đổi trạng thái",
              isUpdating: false,
            });
            showToast.error(response.message || "Lỗi khi thay đổi trạng thái");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi thay đổi trạng thái",
            isUpdating: false,
          });
          showToast.error(error.message || "Lỗi khi thay đổi trạng thái");
        }
      },

      // Toggle page display
      toggleDisplay: async (id: string) => {
        set({ isUpdating: true, error: null });
        try {
          const response: any = await pageService.toggleDisplay(id);
          if (response.ok && response.data) {
            const { pages } = get();
            const updatedPages = pages.map((page) =>
              page._id === id ? response.data : page
            );
            set({
              pages: updatedPages,
              selectedPage: response.data,
              isUpdating: false,
            });
            showToast.success("Thay đổi hiển thị thành công");
          } else {
            set({
              error: response.message || "Lỗi khi thay đổi hiển thị",
              isUpdating: false,
            });
            showToast.error(response.message || "Lỗi khi thay đổi hiển thị");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi thay đổi hiển thị",
            isUpdating: false,
          });
          showToast.error(error.message || "Lỗi khi thay đổi hiển thị");
        }
      },

      // Set selected page
      setSelectedPage: (page: Page | null) => {
        set({ selectedPage: page });
      },

      // Clear selected page
      clearSelectedPage: () => {
        set({ selectedPage: null });
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },

      // Set current page
      setCurrentPage: (page: number) => {
        set((state) => ({
          pagination: { ...state.pagination, page },
        }));
      },
    }),
    {
      name: "page-storage",
      partialize: (state) => ({
        pagination: state.pagination,
      }),
    }
  )
);
